import type { MatchSuggestion } from 'src/connected-assets/types';

const now = new Date().toISOString();
let counter = 0;

function suggestion(overrides: {
  feedRef: string;
  assetRef: string;
  clientName: string;
  feedId?: string;
  assetId?: string;
  clientId?: string;
  feedProductName?: string;
  feedProductType?: string;
  feedSchemeName?: string | null;
  assetDescription?: string;
  assetProduct?: string;
  feedValue?: number | null;
  assetValue?: number | null;
  reasons?: MatchSuggestion['matchReasons'];
  status?: MatchSuggestion['status'];
  confidence?: MatchSuggestion['confidence'];
}): MatchSuggestion {
  counter++;
  const id = `sandbox-${counter}`;
  return {
    id,
    confidence: overrides.confidence ?? 'HIGH',
    status: overrides.status ?? 'PENDING',
    origin: 'SUGGESTION',
    resolvedBy: overrides.status && overrides.status !== 'PENDING' ? 'resolver-id' : null,
    resolvedAt: overrides.status && overrides.status !== 'PENDING' ? now : null,
    createdAt: now,
    unprocessedProduct: {
      id: overrides.feedId ?? `feed-${id}`,
      providerReferenceNumber: overrides.feedRef,
      productName: overrides.feedProductName ?? 'Stakeholder Pension',
      productType: overrides.feedProductType ?? 'PENSION',
      schemeName: overrides.feedSchemeName ?? null,
      provider: 'Standard Life',
      valuationsProvider: 'FINIO',
      value: overrides.feedValue ?? 52_000,
      valueDate: '2026-03-25',
    },
    suggestedAsset: {
      id: overrides.assetId ?? `asset-${id}`,
      providerReferenceNumber: overrides.assetRef,
      clientId: overrides.clientId ?? `client-${id}`,
      clientName: overrides.clientName,
      description: overrides.assetDescription ?? 'Pension',
      product: overrides.assetProduct ?? 'DEFINED_CONTRIBUTION_PENSION',
      value: overrides.assetValue ?? 48_000,
    },
    matchReasons: overrides.reasons ?? [{ type: 'KNOWN_AFFIX', detail: "stripped prefix 'SL'" }],
  };
}

export const HIGH_CONFIDENCE_MANY: MatchSuggestion[] = [
  suggestion({
    feedRef: 'SL-UNMATCHED-001',
    assetRef: 'UNMATCHED-001',
    clientName: 'Bob Unmatched',
    feedProductName: 'SL Group Pension',
    feedProductType: 'PENSION',
    feedSchemeName: 'SL Scheme',
    assetDescription: 'Bob Manual Pension',
    feedValue: 80_000,
    assetValue: 90_000,
  }),
  suggestion({
    feedRef: 'WP67890',
    assetRef: '67890',
    clientName: 'Sarah Johnson',
    feedProductName: 'Personal Pension Plan',
    assetDescription: 'Personal Pension',
    feedValue: 125_000,
    assetValue: 118_000,
    reasons: [{ type: 'KNOWN_AFFIX', detail: "stripped prefix 'WP'" }],
  }),
  suggestion({
    feedRef: 'BV-FIDELITY-003',
    assetRef: 'FIDELITY-003',
    clientName: 'Bob Unmatched',
    feedProductName: 'Fidelity ISA',
    feedProductType: 'ISA',
    feedSchemeName: 'Fidelity ISA',
    assetDescription: 'Bob Fidelity ISA',
    assetProduct: 'INVESTMENT_AND_CASH',
    feedValue: 45_000,
    assetValue: 30_000,
    reasons: [{ type: 'KNOWN_AFFIX', detail: "stripped prefix 'BV'" }],
  }),
  suggestion({
    feedRef: 'SCC907765SIPP',
    assetRef: '907765',
    clientName: 'Emma Brown',
    feedProductName: 'AJ Bell SIPP',
    feedProductType: 'PENSION',
    assetDescription: 'Emma AJ Bell Pension',
    feedValue: 28_500,
    assetValue: 27_200,
    reasons: [{ type: 'KNOWN_AFFIX', detail: "stripped prefix 'SCC' and suffix 'SIPP'" }],
  }),
  suggestion({
    feedRef: 'FPENS000D476',
    assetRef: '000D476',
    clientName: 'Diana Multiasset',
    feedProductName: 'Fidelity Pension Plan',
    feedSchemeName: 'Fidelity Pens',
    assetDescription: 'Diana Fidelity Pension',
    feedValue: 40_000,
    assetValue: 45_000,
    reasons: [{ type: 'KNOWN_AFFIX', detail: "stripped prefix 'FPENS'" }],
  }),
  suggestion({
    feedRef: 'WP-44321',
    assetRef: '44321',
    clientName: 'Michael Davis',
    feedProductName: 'Group Personal Pension',
    assetDescription: 'Michael GPP',
    feedValue: 67_800,
    assetValue: 65_100,
    reasons: [{ type: 'KNOWN_AFFIX', detail: "stripped prefix 'WP'" }],
  }),
  suggestion({
    feedRef: 'BV55512',
    assetRef: '55512',
    clientName: 'Robert Wilson',
    feedProductName: 'Drawdown Pension',
    assetDescription: 'Income Drawdown',
    assetProduct: 'INCOME_DRAWDOWN_PENSION',
    feedValue: 89_000,
    assetValue: 85_000,
    reasons: [{ type: 'KNOWN_AFFIX', detail: "stripped prefix 'BV'" }],
  }),
  suggestion({
    feedRef: 'WP77234',
    assetRef: '77234',
    clientName: 'Jennifer Moore',
    feedProductName: 'Stakeholder Pension',
    assetDescription: 'Jennifer Pension',
    feedValue: null,
    assetValue: 41_000,
    reasons: [{ type: 'KNOWN_AFFIX', detail: "stripped prefix 'WP'" }],
  }),
];

export const HIGH_CONFIDENCE_FEW: MatchSuggestion[] = HIGH_CONFIDENCE_MANY.slice(0, 2);

export const HIGH_CONFIDENCE_WITH_RESOLVED: MatchSuggestion[] = [
  ...HIGH_CONFIDENCE_MANY.slice(0, 3),
  suggestion({
    feedRef: 'WP11111',
    assetRef: '11111',
    clientName: 'Alan Cooper',
    feedProductName: 'Personal Pension',
    assetDescription: 'Alan PP',
    status: 'CONFIRMED',
  }),
  suggestion({
    feedRef: 'BV22222',
    assetRef: '22222',
    clientName: 'Claire Evans',
    feedProductName: 'SIPP',
    assetDescription: 'Claire SIPP',
    status: 'REJECTED',
    reasons: [{ type: 'KNOWN_AFFIX', detail: "stripped prefix 'BV'" }],
  }),
];

export const HIGH_CONFIDENCE_MULTI_REASON: MatchSuggestion[] = [
  suggestion({
    feedRef: 'SCC907765SIPP',
    assetRef: '907765',
    clientName: 'Emma Brown',
    feedProductName: 'AJ Bell SIPP',
    assetDescription: 'Emma AJ Bell Pension',
    reasons: [
      { type: 'KNOWN_AFFIX', detail: "stripped prefix 'SCC' and suffix 'SIPP'" },
      { type: 'SUBSTRING_CONTAINMENT', detail: 'candidate ref is contained in source ref' },
    ],
  }),
  suggestion({
    feedRef: 'WP-ABC-12345',
    assetRef: '12345',
    clientName: 'Tom Harris',
    feedProductName: 'Group Pension',
    assetDescription: 'Tom Group Pension',
    reasons: [{ type: 'KNOWN_AFFIX', detail: "stripped prefix 'WP'" }],
  }),
];

export const HIGH_CONFIDENCE_EMPTY: MatchSuggestion[] = [];

// --- Ambiguous match fixtures ---
// These demonstrate the three ways ambiguity arises:
// fan-out (1 feed → N assets), fan-in (N feeds → 1 asset), and complex (connected component).

export const AMBIGUOUS_MATCHES: MatchSuggestion[] = [
  // Group 1 — Fan-out: feed SL-99001 matches two different assets
  suggestion({
    feedRef: 'SL-99001',
    assetRef: '99001',
    clientName: 'Alice Brown',
    feedId: 'feed-fanout-1',
    feedProductName: 'Standard Life Pension',
    assetDescription: 'Alice Personal Pension',
    confidence: 'MANUAL_REVIEW',
    feedValue: 52_000,
    assetValue: 48_000,
    reasons: [{ type: 'KNOWN_AFFIX', detail: "stripped prefix 'SL'" }],
  }),
  suggestion({
    feedRef: 'SL-99001',
    assetRef: '990010',
    clientName: 'David Green',
    feedId: 'feed-fanout-1',
    feedProductName: 'Standard Life Pension',
    assetDescription: 'David Stakeholder Pension',
    confidence: 'MANUAL_REVIEW',
    feedValue: 52_000,
    assetValue: 55_000,
    reasons: [
      { type: 'SUBSTRING_CONTAINMENT', detail: 'candidate ref is contained in source ref' },
    ],
  }),

  // Group 2 — Fan-in: two feed products match the same asset on Carol
  suggestion({
    feedRef: 'WP-55555',
    assetRef: '55555',
    clientName: 'Carol White',
    assetId: 'asset-fanin-1',
    clientId: 'client-carol',
    feedProductName: 'Wrap Pension',
    assetDescription: 'Carol Pension Plan',
    confidence: 'MANUAL_REVIEW',
    feedValue: 80_000,
    assetValue: 75_000,
    reasons: [{ type: 'KNOWN_AFFIX', detail: "stripped prefix 'WP'" }],
  }),
  suggestion({
    feedRef: 'BV55555',
    assetRef: '55555',
    clientName: 'Carol White',
    assetId: 'asset-fanin-1',
    clientId: 'client-carol',
    feedProductName: 'Bulk Val Pension',
    assetDescription: 'Carol Pension Plan',
    confidence: 'MANUAL_REVIEW',
    feedValue: 78_000,
    assetValue: 75_000,
    reasons: [{ type: 'KNOWN_AFFIX', detail: "stripped prefix 'BV'" }],
  }),

  // Group 3 — Complex connected component:
  // FPENS77001 matches both 77001 (Eve) and 77001B (Frank)
  // SCC77001BSIPP also matches 77001B (Frank)
  // All three are connected through the shared asset 77001B.
  suggestion({
    feedRef: 'FPENS77001',
    assetRef: '77001',
    clientName: 'Eve Thompson',
    feedId: 'feed-complex-1',
    assetId: 'asset-complex-1',
    clientId: 'client-eve',
    feedProductName: 'Fidelity Pension',
    assetDescription: 'Eve Fidelity Pension',
    confidence: 'MANUAL_REVIEW',
    feedValue: 40_000,
    assetValue: 42_000,
    reasons: [{ type: 'KNOWN_AFFIX', detail: "stripped prefix 'FPENS'" }],
  }),
  suggestion({
    feedRef: 'FPENS77001',
    assetRef: '77001B',
    clientName: 'Frank Harris',
    feedId: 'feed-complex-1',
    assetId: 'asset-complex-2',
    clientId: 'client-frank',
    feedProductName: 'Fidelity Pension',
    assetDescription: 'Frank Fidelity Pension',
    confidence: 'MANUAL_REVIEW',
    feedValue: 40_000,
    assetValue: 38_000,
    reasons: [
      { type: 'SUBSTRING_CONTAINMENT', detail: 'candidate ref is contained in source ref' },
    ],
  }),
  suggestion({
    feedRef: 'SCC77001BSIPP',
    assetRef: '77001B',
    clientName: 'Frank Harris',
    feedId: 'feed-complex-2',
    assetId: 'asset-complex-2',
    clientId: 'client-frank',
    feedProductName: 'AJ Bell SIPP',
    assetDescription: 'Frank Fidelity Pension',
    confidence: 'MANUAL_REVIEW',
    feedValue: 35_000,
    assetValue: 38_000,
    reasons: [{ type: 'KNOWN_AFFIX', detail: "stripped prefix 'SCC' and suffix 'SIPP'" }],
  }),
];
